import { createAsyncThunk } from '@reduxjs/toolkit';
import { sendGetRequest, sendPatchRequest } from '../../../api/requestUtil';

export const patchArticlePin = createAsyncThunk<
  ArticleCard,
  { articleId: number; pin_weight?: number; pinned_until: string | null },
  { state: AppState; rejectValue: { detail: string } }
>(
  'adminArticles/patchArticlePin',
  async ({ articleId, pin_weight, pinned_until }, { getState, rejectWithValue }) => {
    const { user } = getState();
    if (!user.isStaff || !user.authToken) {
      return rejectWithValue({ detail: 'Permission denied.' });
    }
    const body: { pin_weight?: number; pinned_until: string | null } = { pinned_until };
    if (pin_weight !== undefined) body.pin_weight = pin_weight;
    try {
      return await sendPatchRequest(`/api/articles/${articleId}/pin/`, body, user.authToken);
    } catch (error: any) {
      return rejectWithValue({ detail: error.response?.data?.detail || 'Failed to update pin.' });
    }
  }
);

interface BlogToggleResponse {
  detail: string;
  is_active: boolean;
  is_allowed: boolean;
}

export const getAdminBlogList = createAsyncThunk<
  BlogListSuccessType,
  { page?: number; filter?: 'all' | 'banned' | 'inactive' },
  { state: AppState; rejectValue: { detail: string } }
>(
  'adminBlogs/getAdminBlogList',
  async ({ page = 1, filter = 'all' }, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.user.authToken;

    if (!state.user.isStaff || !token) {
      return rejectWithValue({
        detail: 'Permission denied. Must be authenticated staff.',
      });
    }

    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', page.toString());
      if (filter !== 'all') {
        params.append('filter', filter);
      }

      const response = await sendGetRequest(
        `/api/admin/blogs/list/?${params.toString()}`,
        token
      );
      return response;
    } catch (error: any) {
      const message =
        error.response?.data?.detail || 'Failed to fetch admin blog list.';
      return rejectWithValue({ detail: message });
    }
  }
);

export const getAdminRecentBlogs = createAsyncThunk<
  BlogListSuccessType,
  { page?: number },
  { state: AppState; rejectValue: { detail: string } }
>(
  'adminRecentBlogs/getAdminRecentBlogs',
  async ({ page = 1 }, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.user.authToken;

    if (!state.user.isStaff || !token) {
      return rejectWithValue({
        detail: 'Permission denied. Must be authenticated staff.',
      });
    }

    try {
      const response = await sendGetRequest(
        `/api/admin/blogs/recent/?page=${page}`,
        token
      );
      return response;
    } catch (error: any) {
      const message =
        error.response?.data?.detail || 'Failed to fetch recent blogs.';
      return rejectWithValue({ detail: message });
    }
  }
);

// Fetch single blog details (admin only - bypasses is_active/is_allowed filters)
export const getAdminBlogDetails = createAsyncThunk<
  BlogCard,
  number, // blog ID
  { state: AppState; rejectValue: { detail: string } }
>(
  'adminBlogs/getAdminBlogDetails',
  async (blogId, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.user.authToken;

    if (!state.user.isStaff || !token) {
      return rejectWithValue({
        detail: 'Permission denied. Must be authenticated staff.',
      });
    }

    try {
      const response = await sendGetRequest(
        `/api/blogs/${blogId}/admin-details/`,
        token
      );
      return response;
    } catch (error: any) {
      const message =
        error.response?.data?.detail || 'Failed to fetch blog details.';
      return rejectWithValue({ detail: message });
    }
  }
);

// Update blog moderation status (ban/unban, activate/deactivate)
export const updateBlogModeration = createAsyncThunk<
  BlogCard,
  { blogId: number; payload: BlogModerationUpdatePayload },
  { state: AppState; rejectValue: { detail: string } }
>(
  'adminBlogs/updateBlogModeration',
  async ({ blogId, payload }, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.user.authToken;

    if (!state.user.isStaff || !token) {
      return rejectWithValue({
        detail: 'Permission denied. Must be authenticated staff.',
      });
    }

    try {
      const response = await sendPatchRequest(
        `/api/blogs/${blogId}/moderate/`,
        payload,
        token
      );
      return response;
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        'Failed to update blog moderation status.';
      return rejectWithValue({ detail: message });
    }
  }
);

// 🟢 Toggle Blog Active Status
export const toggleBlogActiveStatus = createAsyncThunk<
  BlogToggleResponse,
  number,
  { state: AppState }
>('admin/toggleActive', async (blogId, { getState, rejectWithValue }) => {
  const token = getState().user.authToken;
  try {
    const response = await sendPatchRequest(
      `/api/blogs/${blogId}/toggle-active/`,
      {},
      token
    );
    return response as BlogToggleResponse;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || { detail: 'Failed to toggle active status' }
    );
  }
});

// 🟢 Toggle Blog Allowed Status
export const toggleBlogAllowedStatus = createAsyncThunk<
  BlogToggleResponse,
  number,
  { state: AppState }
>('admin/toggleAllowed', async (blogId, { getState, rejectWithValue }) => {
  const token = getState().user.authToken;
  try {
    const response = await sendPatchRequest(
      `/api/blogs/${blogId}/toggle-allowed/`,
      {},
      token
    );
    return response as BlogToggleResponse;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || { detail: 'Failed to toggle allowed status' }
    );
  }
});

// export const getAdminBlogList = createAsyncThunk<
//   BlogListSuccessType, // The shape of the data returned on success
//   void, // The type of the argument passed to the thunk (none needed here)
//   { state: AppState; rejectValue: { detail: string } } // ThunkAPI Configuration
// >('blogs/getAdminBlogList', async (_, { getState, rejectWithValue }) => {
//   const state = getState();
//   const token = state.user.authToken; // 🟢 Retrieve the authentication token

//   // Optional: Client-side guard check before making the request
//   if (!state.user.isStaff || !token) {
//     return rejectWithValue({
//       detail: 'Permission denied. Must be authenticated staff.',
//     });
//   }

//   try {
//     // Use the utility function to make the GET request, passing the token
//     const response = await sendGetRequest('/api/admin/blogs/list/', token);

//     return response;
//   } catch (error: any) {
//     // Handle server rejection (e.g., 403 Forbidden if token is invalid or expired)
//     const message =
//       error.response?.data?.detail ||
//       'Failed to fetch the administrator blog list.';
//     return rejectWithValue({ detail: message });
//   }
// });
